import { useEffect, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Printer, Download } from 'lucide-react'
import QRCode from 'qrcode'
import { subeQrIcerik } from '@/lib/sube-qr'
import type { Sube } from '@/types'

interface Props {
  acik: boolean
  sube: Sube | null
  onKapat: () => void
}

export function SubeQrModal({ acik, sube, onKapat }: Props) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [yukleniyor, setYukleniyor] = useState(false)

  useEffect(() => {
    if (!acik || !sube) {
      setQrDataUrl(null)
      return
    }
    setYukleniyor(true)
    const icerik = subeQrIcerik(sube.id)
    QRCode.toDataURL(icerik, { width: 600, margin: 2, errorCorrectionLevel: 'H' })
      .then((url) => setQrDataUrl(url))
      .catch((e) => console.error(e))
      .finally(() => setYukleniyor(false))
  }, [acik, sube])

  function indir() {
    if (!qrDataUrl || !sube) return
    const link = document.createElement('a')
    link.href = qrDataUrl
    link.download = `shiftbeat-qr-${sube.isim.replace(/\s+/g, '-')}.png`
    link.click()
  }

  function yazdir() {
    if (!qrDataUrl || !sube) return
    const w = window.open('', '_blank')
    if (!w) return
    w.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${sube.isim} — Shift Beat QR</title>
        <style>
          @page { size: A4; margin: 1cm; }
          body { font-family: system-ui; text-align: center; margin: 0; padding: 2cm 1cm; }
          h1 { font-size: 32pt; margin: 0 0 0.5cm; color: #4f46e5; }
          h2 { font-size: 20pt; margin: 0 0 1cm; color: #1f2937; }
          img { max-width: 80%; height: auto; }
          .footer { margin-top: 1cm; color: #6b7280; font-size: 10pt; }
          .instr { margin-top: 0.5cm; padding: 1cm; background: #f3f4f6; border-radius: 16px; max-width: 16cm; margin-left: auto; margin-right: auto; text-align: left; }
        </style>
      </head>
      <body>
        <h1>Shift Beat</h1>
        <h2>${sube.isim}</h2>
        <img src="${qrDataUrl}" />
        <div class="instr">
          <p><strong>Personel:</strong> Bu QR'ı telefonunla okutarak giriş/çıkış yap.</p>
          <p>İlk seferinde kameranın açılması için izin ver.</p>
        </div>
        <div class="footer">${new Date().toLocaleDateString('tr-TR')} • Shift Beat</div>
      </body>
      </html>
    `)
    w.document.close()
    setTimeout(() => w.print(), 500)
  }

  return (
    <Modal
      acik={acik}
      onKapat={onKapat}
      baslik={sube ? `${sube.isim} — Şube QR` : 'Şube QR'}
      boyut="md"
    >
      <div className="space-y-4">
        <div className="text-center text-sm text-gray-600">
          Bu QR'ı yazdırıp şubenin görünür bir yerine yapıştır. Personel telefonu ile okutarak giriş/çıkış yapacak.
        </div>

        {yukleniyor && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
          </div>
        )}

        {qrDataUrl && (
          <div className="bg-white border-2 border-gray-200 rounded-2xl p-4 flex justify-center">
            <img src={qrDataUrl} alt="Şube QR" className="w-72 h-72" />
          </div>
        )}

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
          ⚠️ Bu QR <strong>kalıcıdır</strong> — şubenin değişmemiş hali için tek bir QR yeter. Şubenin GPS konumunu değiştirirsen yeni QR yaratmana gerek yok, eski QR çalışmaya devam eder.
        </div>

        <div className="flex gap-2 pt-2">
          <button onClick={indir} className="btn-secondary flex-1 flex items-center justify-center gap-2">
            <Download className="w-4 h-4" />
            <span>İndir</span>
          </button>
          <button onClick={yazdir} className="btn-primary flex-1 flex items-center justify-center gap-2">
            <Printer className="w-4 h-4" />
            <span>Yazdır</span>
          </button>
        </div>
      </div>
    </Modal>
  )
}
